import { z } from 'zod';
import type {
  AddProposedActionModificationT,
  ApplyPlanModificationsRequestTModificationsItem,
  JsonValue,
  ProposedActionPayloadT,
  RemoveProposedActionModificationT,
  UpdateProposedActionFieldsT,
  UpdateProposedActionModificationT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { rxDraftToolInputSchema } from '@/entities/rx/model/schema';
import { logger } from '@/shared/lib';

import { draftExistingLabToolInputSchema } from '../draft-existing-lab/model/draftExistingLabFromToolInput';

const sendFollowUpToolInputSchema = z.object({
  title: z.string().min(1),
  content: z.string().min(1),
  instrument_id: z.string().nullable().optional(),
  request_id: z.string().nullable().optional(),
});

export const decisionSchema = z.enum(['Pending', 'Approved', 'Rejected']);
export const nonEmptyStringSchema = z.string().min(1);

export const linkSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('patient'),
  }),
  z.object({
    type: z.literal('problem'),
    id: z.string().uuid(),
  }),
  z.object({
    type: z.literal('request'),
    id: z.string().uuid(),
  }),
]);

const payloadBaseSchema = z.object({
  payload_id: nonEmptyStringSchema,
  payload_justification: nonEmptyStringSchema,
});

const prescriptionPayloadSchema = payloadBaseSchema
  .extend({
    payload_tool_name: z.literal('draft_prescription'),
  })
  .merge(rxDraftToolInputSchema);

const labPayloadSchema = payloadBaseSchema
  .extend({
    payload_tool_name: z.literal('draft_existing_lab'),
  })
  .merge(draftExistingLabToolInputSchema);

const followUpPayloadSchema = payloadBaseSchema
  .extend({
    payload_tool_name: z.literal('send_follow_up'),
  })
  .merge(sendFollowUpToolInputSchema);

export const proposedActionPayloadSchema = z.discriminatedUnion('payload_tool_name', [
  prescriptionPayloadSchema,
  labPayloadSchema,
  followUpPayloadSchema,
]);

export type ProposedActionPayloadInput = z.infer<typeof proposedActionPayloadSchema>;
export type LinkInput = z.infer<typeof linkSchema>;
export type DecisionInput = z.infer<typeof decisionSchema>;
export type ApiModification = ApplyPlanModificationsRequestTModificationsItem;
export type ApiAddModification = AddProposedActionModificationT;
export type ApiUpdateModification = UpdateProposedActionModificationT;
export type ApiRemoveModification = RemoveProposedActionModificationT;

export function toApiPayload(payload: ProposedActionPayloadInput): ProposedActionPayloadT {
  const { payload_id, payload_justification, payload_tool_name, ...syntheticToolInputJson } =
    payload;

  return {
    payload_id,
    tool_name: payload_tool_name,
    tool_input: syntheticToolInputJson as Record<string, JsonValue>,
    justification: payload_justification,
  };
}

export function createUpdateFields(
  fields: Partial<UpdateProposedActionFieldsT>
): UpdateProposedActionFieldsT {
  return fields;
}

export function buildApplyModificationsErrorText(error: unknown): string {
  const axiosDetail = (error as { response?: { data?: unknown } })?.response?.data;
  const message = error instanceof Error ? error.message : 'Unknown error';

  logger.error('copilot.proposedActions.failed', {
    error: message,
    detail: axiosDetail,
  });

  if (axiosDetail) {
    return `Failed to apply modifications:\n${JSON.stringify(axiosDetail, null, 2)}`;
  }

  return `Failed to apply proposed action modifications: ${message}`;
}
